import { z } from 'zod';
import { apiRequest } from './core/client';

// Sender alias rules: every `${localPrefix}*@${domain}` address resolves to
// one canonical sender (server: services/senderAliasService.js).

const SenderAliasSchema = z.object({
  id: z.coerce.string(),
  domain: z.string(),
  localPrefix: z.string(),
  canonicalAddress: z.string(),
  state: z.literal('active'),
  createdAt: z.coerce.date(),
});
export type SenderAlias = z.infer<typeof SenderAliasSchema>;

const SenderAliasSuggestionSchema = z.object({
  domain: z.string(),
  localPrefix: z.string(),
  suggestedCanonicalAddress: z.string(),
  displayName: z.string().nullable(),
  addressCount: z.number(),
  pendingCount: z.number(),
  approvedCount: z.number(),
  blockedCount: z.number(),
  sampleAddresses: z.array(z.string()),
});
export type SenderAliasSuggestion = z.infer<typeof SenderAliasSuggestionSchema>;

const CreateSenderAliasResponseSchema = z.object({
  alias: SenderAliasSchema,
  merged: z.array(
    z.object({
      accountId: z.coerce.string(),
      contactId: z.coerce.string().nullable(),
      approvedState: z.enum(['approved', 'blocked', 'pending']).nullable(),
      mergedAddresses: z.number(),
    })
  ),
});
export type CreateSenderAliasResponse = z.infer<typeof CreateSenderAliasResponseSchema>;

export async function fetchSenderAliases(signal?: AbortSignal): Promise<SenderAlias[]> {
  const data = await apiRequest<unknown>({ path: '/sender-aliases', signal });
  return z.array(SenderAliasSchema).parse(data);
}

export async function fetchSenderAliasSuggestions(
  options: { pendingOnly?: boolean; signal?: AbortSignal } = {}
): Promise<SenderAliasSuggestion[]> {
  const data = await apiRequest<unknown>({
    path: '/sender-aliases/suggestions',
    query: { pending_only: options.pendingOnly ? 'true' : undefined },
    signal: options.signal,
  });
  return z.array(SenderAliasSuggestionSchema).parse(data);
}

export async function createSenderAlias(input: {
  domain: string;
  localPrefix: string;
  canonicalAddress: string;
}): Promise<CreateSenderAliasResponse> {
  const data = await apiRequest<unknown>({ path: '/sender-aliases', method: 'POST', body: input });
  return CreateSenderAliasResponseSchema.parse(data);
}

export async function dismissSenderAliasSuggestion(input: {
  domain: string;
  localPrefix: string;
}): Promise<void> {
  await apiRequest<unknown>({ path: '/sender-aliases/suggestions/dismiss', method: 'POST', body: input });
}

export async function deleteSenderAlias(id: string): Promise<void> {
  await apiRequest<unknown>({ path: `/sender-aliases/${encodeURIComponent(id)}`, method: 'DELETE' });
}
